import { useState } from 'react';
import {
  useCompPackages, useCompVariablePay, useOrgPositions,
  type CompPackage, type CompComponent, type VariablePay,
} from '../../../hooks';
import { useConfirm } from '../../../components/ui/confirm';
import { useToast } from '../../../components/ui/toast';
import { Modal } from '../../../components/ui/Modal';
import { inputCls, btnPrimary, btnGhost, thb, Field, Select, EmptyState, TableShell, SectionHeader } from './_shared';

/** ค่าตอบแทนรวม — total-comp packages per position + variable pay designs. */

const COMP_TYPE: Record<string, string> = {
  variable: 'ผันแปร', bonus: 'โบนัส', commission: 'คอมมิชชั่น', benefit: 'สวัสดิการ', allowance: 'เบี้ยเลี้ยง',
};
const FREQ: Record<string, string> = { monthly: 'ต่อเดือน', annual: 'ต่อปี', one_time: 'ครั้งเดียว' };
const VP_TYPE: Record<string, string> = { bonus: 'โบนัส', commission: 'คอมมิชชั่น', incentive: 'อินเซนทีฟ', profit_share: 'ส่วนแบ่งกำไร' };
const VP_FREQ: Record<string, string> = { monthly: 'รายเดือน', quarterly: 'รายไตรมาส', annual: 'รายปี', on_event: 'ตามเหตุการณ์' };

export function PackagesTab() {
  return (
    <div className="space-y-8">
      <CompPackagesSection />
      <VariablePaySection />
    </div>
  );
}

// ── Total-comp packages ──────────────────────────────────────────────────────

function CompPackagesSection() {
  const packages = useCompPackages();
  const positions = useOrgPositions();
  const confirm = useConfirm();
  const toast = useToast();
  const posName = (id: string) => positions.data?.find((p) => p.id === id)?.title ?? '—';
  const posOpts = (positions.data ?? []).map((p) => ({ id: p.id, name: `${p.title} (L${p.level})` }));
  const rows = packages.data ?? [];

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CompPackage | null>(null);
  const [positionId, setPositionId] = useState('');
  const [base, setBase] = useState('');
  const [notes, setNotes] = useState('');
  const [components, setComponents] = useState<CompComponent[]>([]);

  function openNew() { setEditing(null); setPositionId(''); setBase(''); setNotes(''); setComponents([]); setOpen(true); }
  function openEdit(p: CompPackage) {
    setEditing(p); setPositionId(p.position_id); setBase(String(p.base_salary));
    setNotes(p.notes ?? ''); setComponents(p.components ?? []); setOpen(true);
  }
  function setComp(i: number, patch: Partial<CompComponent>) {
    setComponents((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  }
  function addComp() { setComponents((cs) => [...cs, { type: 'variable', amount: 0, frequency: 'annual' }]); }
  function removeComp(i: number) { setComponents((cs) => cs.filter((_, idx) => idx !== i)); }
  async function onDelete(p: CompPackage) {
    if (!(await confirm({ title: `ลบแพ็กเกจค่าตอบแทนของ "${posName(p.position_id)}"?`, danger: true, confirmText: 'ลบ' }))) return;
    packages.del.mutate(p.id, {
      onSuccess: () => toast.success('ลบแล้ว'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ลบไม่สำเร็จ'),
    });
  }
  function onSubmit() {
    if (!positionId) { toast.error('เลือกตำแหน่ง'); return; }
    const b = Number(base);
    if (!Number.isFinite(b) || b < 0) { toast.error('กรอกเงินเดือนฐาน'); return; }
    const cleanComps = components
      .filter((c) => c.type && Number(c.amount) > 0)
      .map((c) => ({ type: c.type, amount: Number(c.amount), frequency: c.frequency || 'annual' }));
    packages.save.mutate(
      { id: editing?.id, body: { position_id: positionId, base_salary: b, components: cleanComps, notes: notes.trim() || null } },
      {
        onSuccess: () => { toast.success('บันทึกแล้ว'); setOpen(false); },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'),
      },
    );
  }

  return (
    <div className="space-y-3">
      <SectionHeader
        title="แพ็กเกจค่าตอบแทนรวม"
        subtitle="ค่าตอบแทนรวมต่อตำแหน่ง (เงินเดือนฐาน + องค์ประกอบอื่น) พร้อมมูลค่ารวมต่อปี (CTC)"
        action={<button onClick={openNew} className={btnPrimary}>+ เพิ่มแพ็กเกจ</button>}
      />

      {packages.isLoading ? (
        <p className="py-4 text-center text-sm text-gray-400">กำลังโหลด…</p>
      ) : rows.length === 0 ? (
        <EmptyState text="ยังไม่มีแพ็กเกจค่าตอบแทน" />
      ) : (
        <TableShell head={
          <tr>
            <th className="px-3 py-2 font-medium">ตำแหน่ง</th>
            <th className="px-3 py-2 font-medium">เงินเดือนฐาน</th>
            <th className="px-3 py-2 font-medium">ผันแปร/ปี</th>
            <th className="px-3 py-2 font-medium">สวัสดิการ/ปี</th>
            <th className="px-3 py-2 font-medium">รวมต่อปี (CTC)</th>
            <th className="px-3 py-2"></th>
          </tr>
        }>
          {rows.map((p) => (
            <tr key={p.id} className="border-t border-gray-100">
              <td className="px-3 py-2 font-medium text-gray-900">{posName(p.position_id)}</td>
              <td className="px-3 py-2 text-gray-500">{thb(p.base_salary)}</td>
              <td className="px-3 py-2 text-gray-500">{thb(p.variable_total)}</td>
              <td className="px-3 py-2 text-gray-500">{thb(p.benefit_total)}</td>
              <td className="px-3 py-2 font-medium text-gray-900">{thb(p.ctc_total)}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                <button onClick={() => openEdit(p)} className="text-teal-700 hover:underline">แก้ไข</button>
                <button onClick={() => void onDelete(p)} className="ml-3 text-red-600 hover:underline">ลบ</button>
              </td>
            </tr>
          ))}
        </TableShell>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? 'แก้ไขแพ็กเกจค่าตอบแทน' : 'เพิ่มแพ็กเกจค่าตอบแทน'} size="max-w-lg">
        <div className="space-y-3">
          <Field label="ตำแหน่ง"><Select value={positionId} onChange={setPositionId} options={posOpts} placeholder="— เลือกตำแหน่ง —" /></Field>
          <Field label="เงินเดือนฐาน (บาท/เดือน)"><input className={inputCls} type="number" value={base} onChange={(e) => setBase(e.target.value)} /></Field>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-sm font-medium">องค์ประกอบเพิ่มเติม</span>
              <button onClick={addComp} className="text-sm text-teal-700 hover:underline">+ เพิ่มรายการ</button>
            </div>
            {components.length === 0 ? (
              <p className="text-xs text-gray-400">ยังไม่มีองค์ประกอบเพิ่มเติม</p>
            ) : (
              <div className="space-y-2">
                {components.map((c, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-2">
                    <select className={inputCls} value={c.type ?? 'variable'} onChange={(e) => setComp(i, { type: e.target.value })}>
                      {Object.entries(COMP_TYPE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <input className={inputCls} type="number" placeholder="จำนวน" value={c.amount ?? ''} onChange={(e) => setComp(i, { amount: Number(e.target.value) })} />
                    <select className={inputCls} value={c.frequency ?? 'annual'} onChange={(e) => setComp(i, { frequency: e.target.value })}>
                      {Object.entries(FREQ).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <button onClick={() => removeComp(i)} className="px-2 text-red-600" aria-label="ลบรายการ">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Field label="หมายเหตุ (ไม่บังคับ)"><input className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setOpen(false)} className={btnGhost}>ยกเลิก</button>
            <button onClick={onSubmit} disabled={packages.save.isPending} className={btnPrimary}>บันทึก</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Variable pay designs ─────────────────────────────────────────────────────

function VariablePaySection() {
  const vp = useCompVariablePay();
  const positions = useOrgPositions();
  const confirm = useConfirm();
  const toast = useToast();
  const posName = (id: string | null) => (id ? positions.data?.find((p) => p.id === id)?.title ?? '—' : 'ทุกตำแหน่ง');
  const posOpts = (positions.data ?? []).map((p) => ({ id: p.id, name: `${p.title} (L${p.level})` }));
  const rows = vp.data ?? [];

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<VariablePay | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState('bonus');
  const [frequency, setFrequency] = useState('annual');
  const [positionId, setPositionId] = useState('');
  const [notes, setNotes] = useState('');

  function openNew() { setEditing(null); setName(''); setType('bonus'); setFrequency('annual'); setPositionId(''); setNotes(''); setOpen(true); }
  function openEdit(d: VariablePay) {
    setEditing(d); setName(d.name); setType(d.type); setFrequency(d.frequency ?? 'annual');
    setPositionId(d.position_id ?? ''); setNotes(d.notes ?? ''); setOpen(true);
  }
  async function onDelete(d: VariablePay) {
    if (!(await confirm({ title: `ลบ "${d.name}"?`, danger: true, confirmText: 'ลบ' }))) return;
    vp.del.mutate(d.id, {
      onSuccess: () => toast.success('ลบแล้ว'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ลบไม่สำเร็จ'),
    });
  }
  function onSubmit() {
    if (!name.trim()) { toast.error('กรอกชื่อ'); return; }
    vp.save.mutate(
      { id: editing?.id, body: { name: name.trim(), type, frequency, position_id: positionId || null, notes: notes.trim() || null } },
      {
        onSuccess: () => { toast.success('บันทึกแล้ว'); setOpen(false); },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'),
      },
    );
  }

  return (
    <div className="space-y-3">
      <SectionHeader
        title="ค่าตอบแทนผันแปร"
        subtitle="แบบของโบนัส/คอมมิชชั่น/อินเซนทีฟ ตามตำแหน่ง (ออกแบบไว้ก่อน นำไปคำนวณจริงภายหลัง)"
        action={<button onClick={openNew} className={btnPrimary}>+ เพิ่มรายการ</button>}
      />

      {vp.isLoading ? (
        <p className="py-4 text-center text-sm text-gray-400">กำลังโหลด…</p>
      ) : rows.length === 0 ? (
        <EmptyState text="ยังไม่มีค่าตอบแทนผันแปร" />
      ) : (
        <TableShell head={
          <tr>
            <th className="px-3 py-2 font-medium">ชื่อ</th>
            <th className="px-3 py-2 font-medium">ชนิด</th>
            <th className="px-3 py-2 font-medium">ความถี่</th>
            <th className="px-3 py-2 font-medium">ตำแหน่ง</th>
            <th className="px-3 py-2"></th>
          </tr>
        }>
          {rows.map((d) => (
            <tr key={d.id} className="border-t border-gray-100">
              <td className="px-3 py-2 font-medium text-gray-900">{d.name}</td>
              <td className="px-3 py-2 text-gray-500">{VP_TYPE[d.type] ?? d.type}</td>
              <td className="px-3 py-2 text-gray-500">{d.frequency ? VP_FREQ[d.frequency] ?? d.frequency : '—'}</td>
              <td className="px-3 py-2 text-gray-500">{posName(d.position_id)}</td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                <button onClick={() => openEdit(d)} className="text-teal-700 hover:underline">แก้ไข</button>
                <button onClick={() => void onDelete(d)} className="ml-3 text-red-600 hover:underline">ลบ</button>
              </td>
            </tr>
          ))}
        </TableShell>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? 'แก้ไขค่าตอบแทนผันแปร' : 'เพิ่มค่าตอบแทนผันแปร'}>
        <div className="space-y-3">
          <Field label="ชื่อ"><input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="เช่น โบนัสประจำปี" /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="ชนิด">
              <select className={inputCls} value={type} onChange={(e) => setType(e.target.value)}>
                {Object.entries(VP_TYPE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="ความถี่">
              <select className={inputCls} value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                {Object.entries(VP_FREQ).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
          </div>
          <Field label="ตำแหน่ง (เว้นว่าง = ทุกตำแหน่ง)"><Select value={positionId} onChange={setPositionId} options={posOpts} placeholder="— ทุกตำแหน่ง —" /></Field>
          <Field label="เงื่อนไข/หมายเหตุ (ไม่บังคับ)"><input className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setOpen(false)} className={btnGhost}>ยกเลิก</button>
            <button onClick={onSubmit} disabled={vp.save.isPending} className={btnPrimary}>บันทึก</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
